import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, MapPin, Car, Bike, BusFront } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useCommuteCalculator } from '../../hooks/usePracticeHub';
import type { CommuteCalculatorPayload, CommuteCalculatorResult } from '../../api/practiceHub';
import { toast } from 'sonner';

export function CommuteCalculatorPage() {
  const commuteCalculator = useCommuteCalculator();
  const [form, setForm] = useState<CommuteCalculatorPayload>({
    origin: '',
    destination: '',
    mode: 'public',
  });
  const [result, setResult] = useState<CommuteCalculatorResult | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const value = await commuteCalculator.mutateAsync(form);
      setResult(value);
    } catch (error) {
      console.error(error);
      toast.error('Unable to calculate commute right now. Please try again.');
    }
  };

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]"
      aria-labelledby="commute-page-title"
    >
      <header className="border-b border-white/10 bg-[rgba(5,9,19,0.82)] backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <Link
            to="/candidate/dashboard"
            className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </Link>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Clock className="h-4 w-4 text-[#ba9036]" />
            <span>Commute planner</span>
          </div>
        </div>
      </header>

      <main className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-8 lg:flex-row">
        <section className="flex-1 space-y-4" aria-labelledby="commute-page-title">
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader>
              <CardTitle id="commute-page-title" className="flex items-center gap-2 text-white">
                <MapPin className="h-5 w-5 text-[#ba9036]" />
                Commute calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-gray-200">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-400">From</label>
                    <Input
                      value={form.origin}
                      onChange={(event) => setForm((prev) => ({ ...prev, origin: event.target.value }))}
                      placeholder="Postcode or area (e.g. SE1 2AA)"
                      required
                      className="mt-1 bg-white/5 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-400">To</label>
                    <Input
                      value={form.destination}
                      onChange={(event) => setForm((prev) => ({ ...prev, destination: event.target.value }))}
                      placeholder="Postcode or area (e.g. SW1A 2AA)"
                      required
                      className="mt-1 bg-white/5 text-white"
                    />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
                  <div>
                    <label className="text-xs uppercase tracking-wide text-gray-400">Mode</label>
                    <Select
                      value={form.mode ?? 'public'}
                      onValueChange={(value) =>
                        setForm((prev) => ({ ...prev, mode: value as CommuteCalculatorPayload['mode'] }))
                      }
                    >
                      <SelectTrigger className="mt-1 bg-white/5 text-white">
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#020617] text-white">
                        <SelectItem value="public">
                          <span className="inline-flex items-center gap-2">
                            <BusFront className="h-4 w-4" />
                            Public transport
                          </span>
                        </SelectItem>
                        <SelectItem value="drive">
                          <span className="inline-flex items-center gap-2">
                            <Car className="h-4 w-4" />
                            Drive
                          </span>
                        </SelectItem>
                        <SelectItem value="cycle">
                          <span className="inline-flex items-center gap-2">
                            <Bike className="h-4 w-4" />
                            Cycle
                          </span>
                        </SelectItem>
                        <SelectItem value="walk">Walk</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="submit"
                      disabled={commuteCalculator.isPending}
                      className="w-full bg-gold-gradient text-black hover:brightness-110"
                    >
                      {commuteCalculator.isPending ? 'Calculating…' : 'Calculate commute'}
                    </Button>
                  </div>
                </div>
              </form>

              {result && (
                <div className="mt-4 rounded-xl border border-white/10 bg-black/40 p-4 text-sm text-gray-200">
                  <p className="text-lg font-semibold text-white">
                    {result.durationMinutes} minutes • £{result.weeklyCost.toFixed(0)} / week
                  </p>
                  <p className="text-xs text-gray-400">
                    Approx. £{result.annualCost.toFixed(0)} per year • {result.carbonKg.toFixed(1)}kg CO₂
                  </p>
                  <ul className="mt-3 space-y-1 text-xs text-gray-400">
                    {result.insights.map((insight, idx) => (
                      <li key={idx}>• {insight}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        <aside className="w-full space-y-4 lg:w-80">
          <Card className="border-white/10 bg-white/[0.06]">
            <CardHeader>
              <CardTitle className="text-sm text-white">Why commute planning matters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs text-gray-300">
              <p>
                Understanding your commute helps you compare roles like-for-like and make sustainable choices that work
                for you and your family.
              </p>
              <p>
                Use this calculator alongside salary insights to decide whether a role is viable once time, cost, and
                carbon impact are factored in.
              </p>
            </CardContent>
          </Card>
        </aside>
      </main>
    </main>
  );
}

